<template>
  <div class="feedback-requests">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading feedback requests...</span>
      </div>
    </div>

    <div v-else-if="requests && requests.length > 0">
      <div class="row">
        <div v-for="request in requests" :key="request.id" class="col-md-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <div class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 mb-3">
                <div class="d-flex align-items-center">
                  <div class="avatar avatar-md me-3">
                    <span class="avatar-title rounded-circle bg-primary">
                      {{ getInitials(request.requested_from?.full_name || (request.requested_from?.first_name + ' ' + request.requested_from?.last_name)) }}
                    </span>
                  </div>
                  <div>
                    <h6 class="mb-1">{{ request.requested_from?.full_name || (request.requested_from?.first_name + ' ' + request.requested_from?.last_name) }}</h6>
                    <p v-if="request.requested_from?.email" class="text-muted small mb-0">
                      {{ request.requested_from?.email }}
                    </p>
                  </div>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-2 ms-sm-auto mt-2 mt-sm-0 justify-content-start justify-content-sm-end">
                  <!-- Status Badge -->
                  <span :class="getStatusBadgeClass(request.status)" class="p-2 flex-shrink-0 text-nowrap">
                    {{ formatStatus(request.status) }}
                  </span>

                  <!-- Action Buttons -->
                  <button
                      v-if="canProvideFeedback(request)"
                      @click="$emit('provide', request)"
                      class="btn btn-sm btn-primary"
                  >
                    <i class="icon-feather-edit me-1"></i>Provide Feedback
                  </button>
                  <button
                      v-if="isSubmitted(request)"
                      @click="openDetails(request)"
                      class="btn btn-sm btn-outline-primary"
                  >
                    <i class="icon-feather-eye me-1"></i>View Details
                  </button>
                  <button
                      v-if="canRemindReviewer(request)"
                      @click="$emit('remind', request)"
                      class="btn btn-sm btn-outline-warning"
                  >
                    <i class="icon-feather-bell me-1"></i>Send Reminder
                  </button>
                </div>
              </div>

              <!-- Request Details -->
              <div class="mb-3">
                <div class="row small">
                  <div class="col-6">
                    <span class="text-muted">Requested by:</span>
                    <br>
                    <strong>{{ request.requested_by?.full_name || (request.requested_by?.first_name + ' ' + request.requested_by?.last_name) }}</strong>
                  </div>
                  <div class="col-6">
                    <span class="text-muted">Requested on:</span>
                    <br>
                    <strong>{{ formatDate(request.created_at) }}</strong>
                  </div>
                </div>
                <div class="row small mt-2">
                  <div class="col-12">
                    <span class="text-muted">Relationship:</span>
                    <br>
                    <strong>{{ formatRelationship(request.relationship) }}</strong>
                  </div>
                </div>
              </div>

              <!-- Feedback Status -->
              <div v-if="request.status === 'pending'" class="alert alert-warning mb-3">
                <i class="icon-feather-clock me-2"></i>
                Awaiting feedback submission
              </div>

              <div v-else-if="isSubmitted(request)" class="alert alert-success mb-3">
                <i class="icon-feather-check-circle me-2"></i>
                Feedback submitted on {{ formatDate(request.completed_at) }}
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Summary Statistics -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="card bg-light">
            <div class="card-body">
              <h6 class="mb-3">360 Feedback Summary</h6>
              <div class="row text-center">
                <div class="col-md-3">
                  <h4 class="text-primary">{{ totalRequests }}</h4>
                  <p class="text-muted mb-0">Total Requests</p>
                </div>
                <div class="col-md-3">
                  <h4 class="text-success">{{ submittedCount }}</h4>
                  <p class="text-muted mb-0">Submitted</p>
                </div>
                <div class="col-md-3">
                  <h4 class="text-warning">{{ pendingCount }}</h4>
                  <p class="text-muted mb-0">Pending</p>
                </div>
                <div class="col-md-3">
                  <h4 class="text-info">{{ completionRate }}%</h4>
                  <p class="text-muted mb-0">Completion Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-5">
      <i class="icon-feather-users fs-1 text-muted mb-3"></i>
      <h5 class="text-muted">No Feedback Requests</h5>
      <p class="text-muted">No 360 feedback has been requested for this review yet.</p>
    </div>

    <!-- Details Modal -->
    <div v-if="showDetailsModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.5);">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Feedback Provided</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeDetails"></button>
          </div>
          <div class="modal-body" v-if="selectedRequest">
            <!-- Header: reviewer, date, badges -->
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div class="text-muted small">Reviewer</div>
                  <strong>{{ selectedRequest.requested_from?.full_name || (selectedRequest.requested_from?.first_name + ' ' + selectedRequest.requested_from?.last_name) }}</strong>
                </div>
                <div class="text-end">
                  <div class="text-muted small">Submitted</div>
                  <strong>{{ formatDate(selectedRequest.completed_at) }}</strong>
                </div>
              </div>
              <div class="mt-2 d-flex align-items-center gap-2">
                <span class="badge bg-secondary">{{ formatRelationship(selectedRequest.relationship) }}</span>
                <span :class="getStatusBadgeClass(selectedRequest.status)">{{ formatStatus(selectedRequest.status) }}</span>
              </div>
            </div>

            <!-- Summary -->
            <div class="row g-3 mb-4">
              <div class="col-12 col-md-6" v-if="averagePercent(selectedRequest) !== null">
                <div class="border rounded p-3 bg-light h-100">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Average Score</span>
                    <span class="fs-5 fw-semibold">{{ averagePercent(selectedRequest) }}%</span>
                  </div>
                  <div class="progress" style="height: 8px;">
                    <div
                      class="progress-bar"
                      :class="getScoreBarClass(averagePercent(selectedRequest))"
                      :style="`width: ${averagePercent(selectedRequest)}%`"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-3" v-if="selectedRequest.overall_rating">
                <div class="border rounded p-3 h-100 d-flex flex-column justify-content-center">
                  <div class="text-muted small">Overall Rating</div>
                  <div class="mt-1">
                    <span class="badge" :class="getScoreBarClass(averagePercent(selectedRequest))">
                      {{ formatLabel(selectedRequest.overall_rating) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-3" v-if="selectedRequest.recommend_promotion !== undefined && selectedRequest.recommend_promotion !== null">
                <div class="border rounded p-3 h-100 d-flex flex-column justify-content-center">
                  <div class="text-muted small">Promotion Recommendation</div>
                  <div class="mt-1">
                    <span class="badge" :class="getPromotionBadgeClass(selectedRequest.recommend_promotion)">
                      {{ formatLabel(selectedRequest.recommend_promotion) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Categories table -->
            <div v-if="Object.keys(getCategories(selectedRequest)).length" class="mb-4">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0">Category Scores</h6>
              </div>
              <div class="table-responsive">
                <table class="table table-sm align-middle">
                  <thead class="table-light">
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col" style="width: 80px;">Score</th>
                      <th scope="col" style="min-width: 180px;">Progress</th>
                      <th scope="col">Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(score, category) in getCategories(selectedRequest)" :key="category">
                      <td class="text-muted">{{ formatLabel(category) }}</td>
                      <td class="fw-semibold">{{ score }}/5</td>
                      <td>
                        <div class="progress" style="height: 10px;">
                          <div
                            class="progress-bar"
                            :class="getScoreBarClass(score * 20)"
                            :style="`width: ${score * 20}%`"
                          ></div>
                        </div>
                      </td>
                      <td class="text-muted">
                        <small>{{ getCategoryComments(selectedRequest)[category] || '-' }}</small>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Narrative sections -->
            <div class="row g-3">
              <div class="col-md-6" v-if="selectedRequest.strengths">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-2 fw-bold">Strengths</h6>
                    <p class="mb-0">{{ selectedRequest.strengths }}</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6" v-if="selectedRequest.improvements">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-2 fw-bold">Areas for Improvement</h6>
                    <p class="mb-0">{{ selectedRequest.improvements }}</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6" v-if="selectedRequest.additional_comments || selectedRequest.feedback">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-2 fw-bold">Additional Comments</h6>
                    <p class="mb-0">{{ selectedRequest.additional_comments || selectedRequest.feedback }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetails">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import moment from 'moment';

export default {
  name: 'FeedbackRequestsList',
  props: {
    requests: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['provide', 'view', 'remind'],
  setup(props, { emit }) {
    const userId = computed(() => localStorage.getItem('user_id'));
    const userRole = computed(() => localStorage.getItem('user_role'));

    // Modal state
    const showDetailsModal = ref(false);
    const selectedRequest = ref(null);

    const openDetails = (req) => {
      selectedRequest.value = req;
      showDetailsModal.value = true;
      // keep external consumers notified
      emit('view', req);
    };

    const closeDetails = () => {
      showDetailsModal.value = false;
      selectedRequest.value = null;
    };

    const totalRequests = computed(() => props.requests?.length || 0);

    const submittedCount = computed(() =>
      props.requests?.filter(r => r.status === 'completed' || r.status === 'submitted' || !!r.completed_at).length || 0
    );

    const pendingCount = computed(() =>
      props.requests?.filter(r => r.status === 'pending').length || 0
    );

    const completionRate = computed(() => {
      if (totalRequests.value === 0) return 0;
      return Math.round((submittedCount.value / totalRequests.value) * 100);
    });

    const canProvideFeedback = (request) => {
      // The requested user provides feedback when pending
      return request.requested_from?.id === userId.value && request.status === 'pending';
    };

    const canRemindReviewer = (request) => {
      // Manager/HR or the requester can send reminders for pending requests
      return request.status === 'pending' && (
        ['manager', 'hr-manager', 'super-admin'].includes(userRole.value) ||
        request.requested_by?.id === userId.value
      );
    };

    const isSubmitted = (request) => {
      return request?.status === 'completed' || request?.status === 'submitted' || !!request?.completed_at;
    };

    const getInitials = (name) => {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const formatDate = (date) => {
      return moment(date).format('MMM DD, YYYY');
    };

    const formatRelationship = (rel) => {
      if (!rel) return 'N/A';
      return String(rel).replace('_',' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const formatStatus = (status) => {
      const statusMap = {
        pending: 'Pending',
        submitted: 'Submitted',
        completed: 'Submitted',
        declined: 'Declined'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        pending: 'badge bg-warning',
        submitted: 'badge bg-success',
        completed: 'badge bg-success',
        declined: 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const getScoreBarClass = (score) => {
      if (score === 100) return 'bg-success';
      if (score >= 80) return 'bg-primary';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      if (score >= 20) return 'bg-danger';
      return 'bg-secondary';
    };

    // Parse helpers and mappers for feedback details
    const parseJSON = (val) => {
      if (!val) return null;
      if (typeof val === 'string') {
        try { return JSON.parse(val); } catch (e) { return null; }
      }
      if (typeof val === 'object') return val;
      return null;
    };

    const getCategories = (request) => {
      const obj = parseJSON(request?.categories) || {};
      const normalized = {};
      Object.entries(obj).forEach(([k, v]) => {
        const num = Number(v);
        if (!Number.isNaN(num)) normalized[k] = num;
      });
      return normalized;
    };

    const getCategoryComments = (request) => {
      return parseJSON(request?.category_comments) || {};
    };

    const averagePercent = (request) => {
      const fromField = Number(request?.average_score);
      if (!Number.isNaN(fromField) && fromField >= 0) {
        return Math.round(fromField);
      }
      const cats = getCategories(request);
      const values = Object.values(cats);
      if (!values.length) return null;
      const mean5 = values.reduce((a, b) => a + b, 0) / values.length; // 0..5
      return Math.round((mean5 / 5) * 100); // percent 0..100
    };

    const formatLabel = (val) => {
      if (val === null || val === undefined) return '';
      return String(val)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getPromotionBadgeClass = (val) => {
      if (val === 'yes') return 'bg-success';
      if (val === 'no') return 'bg-danger';
      return 'bg-warning'; // maybe
    };

    return {
      userId,
      userRole,
      totalRequests,
      submittedCount,
      pendingCount,
      completionRate,
      canProvideFeedback,
      canRemindReviewer,
      isSubmitted,
      getInitials,
      formatDate,
      formatRelationship,
      formatStatus,
      getStatusBadgeClass,
      getScoreBarClass,
      getCategories,
      getCategoryComments,
      averagePercent,
      formatLabel,
      getPromotionBadgeClass,
      // modal api
      showDetailsModal,
      selectedRequest,
      openDetails,
      closeDetails
    };
  }
};
;
</script>

<style scoped>
.avatar-md {
  width: 48px;
  height: 48px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 1rem;
  font-weight: 600;
  color: white;
}
</style>
